import React, { useState } from 'react';
import { X, Loader2, Upload, ImageIcon, Instagram, Facebook, Save, Plus, Wand2 } from 'lucide-react';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { base44 } from '@/api/base44Client';

const inputCls = 'bg-[#0d0d0f] border-white/10 text-white h-11 placeholder:text-white/20 focus:border-primary/60';

export default function CreateSocialPostModal({ onClose, onCreated }) {
  const [caption, setCaption] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [linkUrl, setLinkUrl] = useState('');
  const [ig, setIg] = useState(true);
  const [fb, setFb] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [seed, setSeed] = useState('');
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const { file_url } = await base44.integrations.Core.UploadFile({ file });
      setImageUrl(file_url);
    } catch (err) {
      setError(err.message || 'Upload failed.');
    } finally {
      setUploading(false);
    }
  };

  const handleAiGenerate = async () => {
    if (!seed.trim() && !imageUrl) {
      setError('Add a basic description or an image first.');
      return;
    }
    setError('');
    setGenerating(true);
    try {
      const prompt = `You are a social media copywriter for Motion Reality, a premium motion racing simulation hardware store and cockpit booking venue in Liverpool, UK. Write a single engaging social media caption for Instagram and Facebook.${seed.trim() ? ` The admin's basic description: "${seed.trim()}".` : ''} Keep it energetic but professional, concise (max ~220 characters), and end with relevant hashtags including #SimRacing #MotionReality. Return only the caption text — no quotes, no preamble.`;
      const res = await base44.integrations.Core.InvokeLLM({
        prompt,
        ...(imageUrl ? { file_urls: [imageUrl] } : {}),
        response_json_schema: { type: 'object', properties: { caption: { type: 'string' } } },
      });
      const generated = (res && typeof res === 'object' ? res.caption : '') || (typeof res === 'string' ? res : '');
      if (generated) setCaption(generated.trim());
      else setError('AI did not return a caption. Try again.');
    } catch (err) {
      setError(err.message || 'AI generation failed.');
    } finally {
      setGenerating(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!caption.trim()) {
      setError('Caption is required.');
      return;
    }
    setError('');
    setSaving(true);
    try {
      await base44.entities.SocialPost.create({
        source_type: 'manual',
        source_label: 'Manual Post',
        caption: caption.trim(),
        image_url: imageUrl,
        link_url: linkUrl.trim(),
        post_to_instagram: ig,
        post_to_facebook: fb,
        status: 'draft',
        created_by_name: 'Admin',
      });
      onCreated();
    } catch (err) {
      setError(err.message || 'Failed to create post.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
      <div className="bg-[#111115] border border-white/10 w-full max-w-xl max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/5 sticky top-0 bg-[#111115] z-10">
          <div className="flex items-center gap-2">
            <Plus className="w-4 h-4 text-primary" />
            <p className="text-primary text-[10px] font-bold tracking-widest">NEW SOCIAL POST</p>
          </div>
          <button onClick={onClose} className="text-white/40 hover:text-white transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSave} className="px-6 py-5 space-y-4">
          {/* Image */}
          <div>
            <p className="text-white/40 text-[10px] font-bold tracking-widest mb-1.5">IMAGE</p>
            <div className="flex gap-3">
              <div className="w-20 h-20 bg-[#0d0d0f] border border-white/10 flex items-center justify-center shrink-0 overflow-hidden">
                {imageUrl ? <img src={imageUrl} alt="" className="w-full h-full object-cover" /> : <ImageIcon className="w-5 h-5 text-white/20" />}
              </div>
              <div className="flex-1 space-y-2">
                <label className="cursor-pointer flex items-center gap-1.5 bg-[#0d0d0f] border border-white/10 hover:border-white/30 text-white/60 hover:text-white text-[10px] font-bold tracking-wider px-3 py-2 w-fit transition-colors">
                  {uploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
                  {uploading ? 'UPLOADING...' : 'UPLOAD'}
                  <input type="file" accept="image/*" className="hidden" onChange={handleUpload} disabled={uploading} />
                </label>
                <Input value={imageUrl} onChange={(e) => setImageUrl(e.target.value)} className={`${inputCls} text-[11px] h-8`} placeholder="or paste image URL" />
              </div>
            </div>
            {ig && !imageUrl && (
              <p className="text-amber-400/70 text-[10px] mt-1.5 flex items-center gap-1">
                <ImageIcon className="w-3 h-3" /> Instagram requires an image — IG will be skipped if left blank.
              </p>
            )}
          </div>

          {/* AI assist */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <p className="text-white/40 text-[10px] font-bold tracking-widest mb-0">BASIC DESCRIPTION <span className="font-normal text-white/20 normal-case">(for AI)</span></p>
              <button
                type="button"
                onClick={handleAiGenerate}
                disabled={generating}
                className="flex items-center gap-1.5 text-[10px] font-bold tracking-wider px-3 py-1.5 border border-primary/40 text-primary bg-primary/10 hover:bg-primary/20 transition-colors disabled:opacity-50"
              >
                {generating ? <Loader2 className="w-3 h-3 animate-spin" /> : <Wand2 className="w-3 h-3" />}
                {generating ? 'GENERATING...' : 'AI CAPTION'}
              </button>
            </div>
            <Textarea value={seed} onChange={(e) => setSeed(e.target.value)} rows={2} className="bg-[#0d0d0f] border-white/10 text-white text-sm resize-y" placeholder="e.g. New Simucube wheel mount, aluminium, fits all rigs — AI turns this into a caption" />
          </div>

          {/* Caption */}
          <div>
            <p className="text-white/40 text-[10px] font-bold tracking-widest mb-1.5">CAPTION <span className="text-red-400">*</span></p>
            <Textarea value={caption} onChange={(e) => setCaption(e.target.value)} rows={5} className="bg-[#0d0d0f] border-white/10 text-white text-sm resize-y" placeholder="Write your post caption..." />
          </div>

          {/* Link */}
          <div>
            <p className="text-white/40 text-[10px] font-bold tracking-widest mb-1.5">LINK URL <span className="font-normal text-white/20 normal-case">(optional)</span></p>
            <Input value={linkUrl} onChange={(e) => setLinkUrl(e.target.value)} className={inputCls} placeholder="https://..." />
          </div>

          {/* Platform toggles */}
          <div className="flex flex-wrap gap-3">
            <button
              type="button"
              onClick={() => setIg(!ig)}
              className={`flex items-center gap-1.5 text-xs font-bold px-3 py-2 border transition-colors ${ig ? 'text-primary border-primary/40 bg-primary/10' : 'text-white/30 border-white/10 hover:border-white/30'}`}
            >
              <Instagram className="w-3.5 h-3.5" /> INSTAGRAM {ig ? 'ON' : 'OFF'}
            </button>
            <button
              type="button"
              onClick={() => setFb(!fb)}
              className={`flex items-center gap-1.5 text-xs font-bold px-3 py-2 border transition-colors ${fb ? 'text-blue-400 border-blue-400/40 bg-blue-400/10' : 'text-white/30 border-white/10 hover:border-white/30'}`}
            >
              <Facebook className="w-3.5 h-3.5" /> FACEBOOK {fb ? 'ON' : 'OFF'}
            </button>
          </div>

          {error && <p className="text-red-400 text-xs">{error}</p>}

          <div className="flex gap-3 pt-3 border-t border-white/5">
            <button type="button" onClick={onClose} className="flex-1 border border-white/10 hover:border-white/30 text-white/50 hover:text-white text-xs font-bold tracking-wider py-3 transition-colors">
              CANCEL
            </button>
            <button type="submit" disabled={saving} className="flex-1 bg-primary hover:bg-primary/90 text-white font-bold text-xs tracking-wider py-3 transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              {saving ? 'SAVING...' : 'CREATE DRAFT'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}